/**
 * Drives the self-service password reset flow against the Laravel SSPR endpoints.
 * Steps: start(email) -> codeSent -> verifyCode(code) -> passwordRequired ->
 * submitPassword(pw) -> completed.
 */
import {useCallback, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {MfaChallengeInfo, resetResend, resetStart, resetSubmit, resetVerify} from './nativeAuthApi';
import {extractErrorCode, nativeAuthErrorMessage} from './errorMessages';

export type ResetStatus =
  | 'idle'
  | 'sendingCode'
  | 'codeSent'
  | 'verifying'
  | 'passwordRequired'
  | 'submitting'
  | 'completed'
  | 'failed';

export interface UseResetPassword {
  status: ResetStatus;
  error?: string;
  /** Transient notice shown on the code step (e.g. a new code was auto-sent after expiry). */
  notice?: string;
  challenge?: MfaChallengeInfo;
  start: (email: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  submitPassword: (newPassword: string) => Promise<void>;
  reset: () => void;
}

export const useResetPassword = (): UseResetPassword => {
  const intl = useIntl();
  const [status, setStatus] = useState<ResetStatus>('idle');
  const [errorCode, setErrorCode] = useState<string>();
  const [noticeCode, setNoticeCode] = useState<string>();
  const [challenge, setChallenge] = useState<MfaChallengeInfo>();
  const continuationRef = useRef<string | null>(null);
  const emailRef = useRef<string>('');

  const start = useCallback(async (email: string): Promise<void> => {
    setErrorCode(undefined);
    setNoticeCode(undefined);
    setStatus('sendingCode');
    emailRef.current = email;
    try {
      const result = await resetStart(email);
      continuationRef.current = result.continuationToken;
      setChallenge(result.challenge);
      setStatus('codeSent');
    } catch (e) {
      setErrorCode(extractErrorCode(e));
      setStatus('failed');
    }
  }, []);

  // Continuation token expired mid-reset. The flow only needs the email (held in
  // emailRef), so silently restart from scratch: a fresh code is sent and the user
  // lands back on the code step with a notice — no error, no manual restart.
  const restartForExpiry = useCallback(async (): Promise<void> => {
    const email = emailRef.current;
    if (!email) {
      setErrorCode('flow_expired');
      setStatus('failed');
      return;
    }
    try {
      const result = await resetStart(email);
      continuationRef.current = result.continuationToken;
      setChallenge(result.challenge);
      setNoticeCode('AUTH.NATIVE.SSPR.CODE_RESENT_EXPIRED');
      setStatus('codeSent');
    } catch (e) {
      setErrorCode(extractErrorCode(e));
      setStatus('failed');
    }
  }, []);

  const verifyCode = useCallback(
    async (code: string): Promise<void> => {
      if (!continuationRef.current) {
        return;
      }
      setErrorCode(undefined);
      setNoticeCode(undefined);
      setStatus('verifying');
      try {
        const result = await resetVerify(continuationRef.current, code);
        continuationRef.current = result.continuationToken;
        setStatus('passwordRequired');
      } catch (e) {
        const errCode = extractErrorCode(e);
        if (errCode === 'flow_expired') {
          await restartForExpiry();
          return;
        }
        setErrorCode(errCode);
        setStatus('codeSent');
      }
    },
    [restartForExpiry]
  );

  const submitPassword = useCallback(
    async (newPassword: string): Promise<void> => {
      if (!continuationRef.current) {
        return;
      }
      setErrorCode(undefined);
      setStatus('submitting');
      try {
        await resetSubmit(continuationRef.current, newPassword);
        setStatus('completed');
      } catch (e) {
        const errCode = extractErrorCode(e);
        if (errCode === 'flow_expired') {
          await restartForExpiry();
          return;
        }
        setErrorCode(errCode);
        setStatus('passwordRequired');
      }
    },
    [restartForExpiry]
  );

  const resendCode = useCallback(async (): Promise<void> => {
    if (!continuationRef.current) {
      return;
    }
    setErrorCode(undefined);
    try {
      const result = await resetResend(continuationRef.current, emailRef.current);
      continuationRef.current = result.continuationToken;
      setChallenge(result.challenge);
      setStatus('codeSent');
    } catch (e) {
      const errCode = extractErrorCode(e);
      if (errCode === 'flow_expired') {
        await restartForExpiry();
        return;
      }
      setErrorCode(errCode);
    }
  }, [restartForExpiry]);

  const reset = useCallback((): void => {
    continuationRef.current = null;
    emailRef.current = '';
    setErrorCode(undefined);
    setNoticeCode(undefined);
    setChallenge(undefined);
    setStatus('idle');
  }, []);

  const error = errorCode ? nativeAuthErrorMessage(intl, errorCode) : undefined;
  const notice = noticeCode ? intl.formatMessage({id: noticeCode}) : undefined;

  return {status, error, notice, challenge, start, verifyCode, resendCode, submitPassword, reset};
};
